import React, { useEffect, useState } from 'react';
import { FadeIn } from '../components/FadeIn';
import { initMetaPixel, trackAddToCart } from '../utils/metaPixel';
import { CheckCircle2, XCircle, Clock, BookOpen, ShieldCheck, Brain, Zap, ListTodo, Timer, Lock, BatteryWarning, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const COVER_IMAGE = 'https://cdn.magicpatterns.com/uploads/gDbimnWW9XyHLm5yK44qQv/portada_landing.png';
function CTAButton({
  className = '',
  text = 'QUIERO EL RESET AHORA',
  scrollTo,
  href
}: {
  className?: string;
  text?: string;
  scrollTo?: string;
  href?: string;
}) {
  if (href) {
    return <motion.a href={href} target="_blank" rel="noopener noreferrer" onClick={() => trackAddToCart()} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className={`bg-navy text-cream px-8 py-4 rounded-full font-sans font-semibold tracking-wide shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer no-underline ${className}`} data-id="element-3">
        {text}
      </motion.a>;
  }
  return <motion.a href={scrollTo ? `#${scrollTo}` : '#offer'} onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById(scrollTo || 'offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  }} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`bg-navy text-cream px-8 py-4 rounded-full font-sans font-semibold tracking-wide shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer no-underline ${className}`} data-id="element-4">
      {text}
    </motion.a>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-navy/10 py-4" data-id="element-5">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between text-left focus:outline-none group" data-id="element-6">
        <span className="font-serif text-lg text-navy group-hover:text-sage transition-colors" data-id="element-7">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-navy transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-8" />
      </button>
      <AnimatePresence data-id="element-9">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-10">
            <p className="pt-4 text-navy/80 font-sans leading-relaxed" data-id="element-11">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function LandingPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen bg-softwhite font-sans text-navy selection:bg-sage/30" data-id="element-12">
      {/* HERO SECTION */}
      <section className="bg-cream pt-20 pb-24 px-6 lg:px-8 overflow-hidden relative" data-id="element-13">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-14">
          <FadeIn direction="right" className="max-w-2xl text-center lg:text-center mx-auto" data-id="element-15">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-tight mb-6" data-id="element-16">
              El Sistema de Reset Somático para Perfeccionistas.
            </h1>
            <p className="text-lg sm:text-xl text-navy/80 mb-10 leading-relaxed" data-id="element-17">
              Si sientes un nudo en el estómago cada vez que abres la laptop, tu
              problema no es la pereza. Es tu sistema nervioso tratando de
              protegerte.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-18">
              <CTAButton data-id="element-19" />
              <span className="text-sm text-navy/60 flex items-center gap-1" data-id="element-20">
                <ShieldCheck className="w-4 h-4" data-id="element-21" /> Pago 100% seguro
              </span>
            </div>
          </FadeIn>

          <FadeIn direction="left" delay={0.2} className="relative mx-auto w-full max-w-md lg:max-w-full" data-id="element-22">
            <div className="absolute inset-0 bg-sage/20 blur-3xl rounded-full transform -translate-x-1/2 translate-y-1/4" data-id="element-23"></div>
            <img src={COVER_IMAGE} alt="El Reset - Una guía para el bienestar" className="relative z-10 w-full h-auto rounded-lg shadow-2xl transform lg:rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-24" />
          </FadeIn>
        </div>
      </section>

      {/* OPENING HOOK & PROBLEM */}
      <section className="bg-softwhite py-24 px-6 lg:px-8" data-id="element-25">
        <div className="max-w-3xl mx-auto" data-id="element-26">
          <FadeIn data-id="element-27">
            <p className="text-xl sm:text-2xl font-serif text-navy leading-relaxed mb-8" data-id="element-28">
              Son las 11 de la noche. Otra vez. Tienes 15 pestañas abiertas, el
              café frío y una culpa demoledora en el pecho por todo lo que no
              hiciste hoy. Sabes exactamente qué tienes que hacer, pero
              simplemente no puedes "hacer clic".
            </p>
            <p className="text-lg text-navy/80 mb-16 font-medium bg-sage/10 p-6 rounded-2xl border border-sage/20" data-id="element-29">
              No necesitas otra app de productividad ni más fuerza de voluntad.
              Necesitas seguridad interna.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-30">
            <h2 className="font-serif text-3xl sm:text-4xl text-navy mb-6" data-id="element-31">
              El problema real no es tu falta de disciplina
            </h2>
            <div className="space-y-6 text-lg text-navy/80 leading-relaxed" data-id="element-32">
              <p data-id="element-33">
                La mayoría de los consejos sobre procrastinación te dicen que
                "solo lo hagas". Pero para ti, esa tarea se siente como un tigre
                en la selva. Tu cerebro ha entrado en modo de supervivencia
                (congelamiento).
              </p>
              <p data-id="element-34">
                Intentas obligarte, te insultas mentalmente y te prometes que
                "mañana será diferente", pero el ciclo siempre se repite porque
                estás tratando un problema emocional con herramientas lógicas.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="bg-navy text-softwhite py-24 px-6 lg:px-8" data-id="element-35">
        <div className="max-w-4xl mx-auto" data-id="element-36">
          <FadeIn data-id="element-37">
            <h2 className="font-serif text-3xl sm:text-4xl mb-12 text-center text-cream" data-id="element-38">
              Por qué lo que has intentado no funciona
            </h2>
          </FadeIn>

          <div className="grid sm:grid-cols-2 gap-8" data-id="element-39">
            {[{
            icon: ListTodo,
            text: 'Las listas de tareas solo aumentan tu ansiedad al ver cuánto te falta.'
          }, {
            icon: Timer,
            text: 'El método Pomodoro te genera pánico cada vez que suena el reloj.'
          }, {
            icon: Lock,
            text: 'Las apps de bloqueo no quitan el miedo a fallar que te mantiene paralizado.'
          }, {
            icon: BatteryWarning,
            text: 'Castigarte drena la poca dopamina que necesitas para empezar.'
          }].map((item, idx) => <FadeIn key={idx} delay={idx * 0.1} className="bg-softwhite/5 p-8 rounded-2xl border border-softwhite/10 flex flex-col gap-4" data-id="element-40">
                <item.icon className="w-8 h-8 text-sage" data-id="element-41" />
                <p className="text-lg text-cream/90" data-id="element-42">{item.text}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM & WHAT YOU GET */}
      <section className="bg-cream py-24 px-6 lg:px-8" data-id="element-43">
        <div className="max-w-5xl mx-auto" data-id="element-44">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-45">
            <FadeIn data-id="element-46">
              <h2 className="font-serif text-3xl sm:text-4xl text-navy mb-6" data-id="element-47">
                Presentamos el Protocolo de Reset Somático
              </h2>
              <p className="text-lg text-navy/80 leading-relaxed mb-6" data-id="element-48">
                A diferencia de los métodos de gestión del tiempo, este sistema
                trabaja con tu biología.
              </p>
              <p className="text-lg text-navy/80 leading-relaxed" data-id="element-49">
                En lugar de empujarte a trabajar, utiliza técnicas de 60
                segundos para "apagar" la señal de alarma de tu amígdala,
                permitiendo que tu cerebro lógico recupere el control de forma
                natural.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="relative" data-id="element-50">
              <div className="aspect-square bg-sage/20 rounded-full absolute -inset-4 blur-3xl" data-id="element-51"></div>
              <div className="bg-softwhite p-8 rounded-3xl shadow-xl relative z-10 border border-navy/5" data-id="element-52">
                <Brain className="w-12 h-12 text-navy mb-6" data-id="element-53" />
                <h3 className="font-serif text-2xl text-navy mb-4" data-id="element-54">
                  Tu cerebro a salvo
                </h3>
                <p className="text-navy/70" data-id="element-55">
                  Pasa del modo supervivencia (congelamiento) al modo ejecutivo
                  (acción) en menos de un minuto.
                </p>
              </div>
            </FadeIn>
          </div>

          <FadeIn data-id="element-56">
            <h2 className="font-serif text-3xl sm:text-4xl text-navy mb-12 text-center" data-id="element-57">
              ¿Qué incluye El Reset?
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-58">
              {[{
              title: 'Manual Principal',
              desc: 'El Reset de la Procrastinación (PDF).'
            }, {
              title: 'Protocolo de Emergencia',
              desc: '3 técnicas de 60 segundos para romper el congelamiento.'
            }, {
              title: 'El Método del Borrador de Basura',
              desc: 'Cómo engañar al perfeccionismo.'
            }, {
              title: 'Checklist de Sostenibilidad',
              desc: 'Para no volver a caer en el ciclo de agotamiento.'
            }].map((item, idx) => <div key={idx} className="bg-softwhite p-6 rounded-2xl shadow-sm border border-navy/5 flex gap-4 items-start" data-id="element-59">
                  <CheckCircle2 className="w-6 h-6 text-sage shrink-0 mt-1" data-id="element-60" />
                  <div data-id="element-61">
                    <h4 className="font-serif text-xl text-navy mb-1" data-id="element-62">
                      {item.title}
                    </h4>
                    <p className="text-navy/70" data-id="element-63">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHO THIS IS FOR & NOT FOR */}
      <section className="bg-softwhite py-24 px-6 lg:px-8" data-id="element-64">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-65">
          <FadeIn data-id="element-66">
            <h2 className="font-serif text-3xl text-navy mb-8 flex items-center gap-3" data-id="element-67">
              <CheckCircle2 className="text-sage w-8 h-8" data-id="element-68" /> Para quién es
            </h2>
            <ul className="space-y-6" data-id="element-69">
              {['Emprendedores y creativos paralizados por el "qué dirán".', 'Estudiantes que posponen hasta que el pánico es insoportable.', 'Personas con sospecha de TDAH que se sienten abrumadas por tareas simples.', 'Cualquier persona que se sienta "vaga" pero sospeche que en realidad tiene miedo.'].map((text, idx) => <li key={idx} className="flex gap-4 text-lg text-navy/80" data-id="element-70">
                  <div className="w-2 h-2 rounded-full bg-sage mt-2.5 shrink-0" data-id="element-71" />
                  <span data-id="element-72">{text}</span>
                </li>)}
            </ul>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-73">
            <h2 className="font-serif text-3xl text-navy mb-8 flex items-center gap-3" data-id="element-74">
              <XCircle className="text-red-400 w-8 h-8" data-id="element-75" /> Para quién NO es
            </h2>
            <ul className="space-y-6" data-id="element-76">
              {['Gente que busca "hacks" de disciplina militar.', 'Personas que no están dispuestas a probar ejercicios de respiración o calma.'].map((text, idx) => <li key={idx} className="flex gap-4 text-lg text-navy/80" data-id="element-77">
                  <div className="w-2 h-2 rounded-full bg-red-400/50 mt-2.5 shrink-0" data-id="element-78" />
                  <span data-id="element-79">{text}</span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-cream py-24 px-6 lg:px-8" data-id="element-80">
        <div className="max-w-4xl mx-auto" data-id="element-81">
          <FadeIn data-id="element-82">
            <h2 className="font-serif text-3xl sm:text-4xl text-navy mb-16 text-center" data-id="element-83">
              Cómo funciona el proceso
            </h2>
          </FadeIn>

          <div className="space-y-12 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-sage/50 before:to-transparent" data-id="element-84">
            {[{
            step: '01',
            title: 'Identifica',
            desc: 'Detectas el nudo en el estómago antes de que te bloquee.'
          }, {
            step: '02',
            title: 'Resetea',
            desc: 'Aplicas el protocolo somático para calmar tu sistema nervioso.'
          }, {
            step: '03',
            title: 'Inicia',
            desc: 'Usas la táctica del "Primer Clic" para avanzar sin fricción.'
          }].map((item, idx) => <FadeIn key={idx} delay={idx * 0.1} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-85">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-cream bg-sage text-softwhite font-serif font-bold shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-md z-10" data-id="element-86">
                  {item.step}
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-softwhite p-6 rounded-2xl shadow-sm border border-navy/5" data-id="element-87">
                  <h3 className="font-serif text-2xl text-navy mb-2" data-id="element-88">
                    {item.title}
                  </h3>
                  <p className="text-navy/70" data-id="element-89">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* PROOF & FAQ */}
      <section className="bg-softwhite py-24 px-6 lg:px-8" data-id="element-90">
        <div className="max-w-3xl mx-auto" data-id="element-91">
          <FadeIn className="text-center mb-20" data-id="element-92">
            <Zap className="w-12 h-12 text-sage mx-auto mb-6" data-id="element-93" />
            <p className="font-serif text-2xl sm:text-3xl text-navy leading-relaxed" data-id="element-94">
              En solo 7 días, pasarás de sentir el trabajo como una amenaza a
              verlo como una serie de pasos manejables. No dejarás de ser
              perfeccionista, pero el perfeccionismo dejará de tener el control
              sobre tus manos.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-95">
            <h2 className="font-serif text-3xl text-navy mb-8" data-id="element-96">
              Preguntas Frecuentes
            </h2>
            <div className="bg-cream rounded-3xl p-8 border border-navy/5" data-id="element-97">
              <FAQItem question="¿Necesito mucho tiempo?" answer="No, las técnicas duran menos de un minuto." data-id="element-98" />
              <FAQItem question="¿Es un curso largo?" answer="No, es un manual directo al grano para leer hoy y usar hoy." data-id="element-99" />
              <FAQItem question="¿Funciona si tengo TDAH?" answer="Sí, está diseñado para cerebros que se abruman fácilmente." data-id="element-100" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER & CTA */}
      <section id="offer" className="bg-navy text-softwhite py-24 px-6 lg:px-8 text-center relative overflow-hidden" data-id="element-101">
        <div className="absolute inset-0 bg-sage/10 blur-[100px] rounded-full transform translate-y-1/2" data-id="element-102"></div>
        <div className="max-w-2xl mx-auto relative z-10" data-id="element-103">
          <FadeIn data-id="element-104">
            <h2 className="font-serif text-4xl sm:text-5xl text-cream mb-6" data-id="element-105">
              Recupera tu paz mental
            </h2>
            <p className="text-xl text-cream/80 mb-10" data-id="element-106">
              Obtén el Sistema Completo de Reset por solo{' '}
              <span className="font-serif text-3xl text-sage font-bold mx-1" data-id="element-107">
                $6.39 USD
              </span>
              . Un pago único para recuperar tu paz mental y tus horas de
              productividad.
            </p>

            <div className="flex flex-col items-center gap-6" data-id="element-108">
              <CTAButton className="!bg-sage !text-navy hover:!bg-sage/90 text-lg px-10 py-5 w-full sm:w-auto" href="https://www.nachogerberg.com/cart/42718250336327:1" data-id="element-109" />
              <div className="flex items-center gap-2 text-cream/60 text-sm" data-id="element-110">
                <Clock className="w-4 h-4" data-id="element-111" /> Acceso instantáneo tras la compra
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <Footer data-id="element-112" />
    </div>;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-113">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" onClick={onClose} data-id="element-114">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3
      }} className="bg-softwhite rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 shadow-2xl" onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-115">
            <div className="flex items-center justify-between mb-6" data-id="element-116">
              <h2 className="font-serif text-2xl text-navy" data-id="element-117">{title}</h2>
              <button onClick={onClose} className="text-navy/50 hover:text-navy transition-colors text-2xl leading-none" data-id="element-118">
                &times;
              </button>
            </div>
            <div className="text-navy/80 leading-relaxed space-y-4 text-sm" data-id="element-119">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="bg-navy border-t border-softwhite/10 py-8 px-6 lg:px-8" data-id="element-120">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-121">
          <p className="text-cream/50 text-sm" data-id="element-122">
            © {new Date().getFullYear()} El Reset. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-123">
            <button onClick={() => setShowPrivacy(true)} className="text-cream/50 hover:text-cream text-sm transition-colors" data-id="element-124">
              Política de Privacidad
            </button>
            <button onClick={() => setShowTerms(true)} className="text-cream/50 hover:text-cream text-sm transition-colors" data-id="element-125">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" data-id="element-126">
        <p data-id="element-127">
          <strong data-id="element-128">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-129">
          En El Reset, respetamos tu privacidad y nos comprometemos a proteger
          tus datos personales. Esta política describe cómo recopilamos, usamos
          y protegemos tu información.
        </p>
        <p data-id="element-130">
          <strong data-id="element-131">Información que recopilamos:</strong> Al realizar tu compra,
          recopilamos tu nombre, dirección de correo electrónico y datos de
          pago. Los datos de pago son procesados de forma segura por nuestro
          proveedor de pagos y no almacenamos información de tarjetas de
          crédito.
        </p>
        <p data-id="element-132">
          <strong data-id="element-133">Uso de la información:</strong> Utilizamos tu información
          únicamente para procesar tu compra, enviarte el producto digital y
          comunicarnos contigo sobre tu pedido.
        </p>
        <p data-id="element-134">
          <strong data-id="element-135">Compartir información:</strong> No vendemos, alquilamos ni
          compartimos tu información personal con terceros, excepto lo necesario
          para procesar tu pago.
        </p>
        <p data-id="element-136">
          <strong data-id="element-137">Seguridad:</strong> Implementamos medidas de seguridad
          estándar de la industria para proteger tu información personal.
        </p>
        <p data-id="element-138">
          <strong data-id="element-139">Tus derechos:</strong> Puedes solicitar acceso, corrección o
          eliminación de tus datos personales en cualquier momento.
        </p>
      </LegalModal>

      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" data-id="element-140">
        <p data-id="element-141">
          <strong data-id="element-142">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-143">
          Al adquirir El Reset, aceptas los siguientes términos y condiciones.
        </p>
        <p data-id="element-144">
          <strong data-id="element-145">Producto digital:</strong> El Reset es un producto digital
          (PDF) que se entrega de forma instantánea tras la compra. Debido a la
          naturaleza digital del producto, no se aceptan devoluciones una vez
          descargado.
        </p>
        <p data-id="element-146">
          <strong data-id="element-147">Uso personal:</strong> El contenido de El Reset es para uso
          personal y no puede ser redistribuido, copiado o compartido sin
          autorización expresa.
        </p>
        <p data-id="element-148">
          <strong data-id="element-149">Propiedad intelectual:</strong> Todo el contenido, incluyendo
          textos, diseños y metodologías, está protegido por derechos de autor.
        </p>
        <p data-id="element-150">
          <strong data-id="element-151">Descargo de responsabilidad:</strong> El Reset es un recurso
          educativo y de bienestar. No sustituye el consejo médico, psicológico
          o profesional. Si experimentas problemas de salud mental, te
          recomendamos consultar con un profesional.
        </p>
        <p data-id="element-152">
          <strong data-id="element-153">Modificaciones:</strong> Nos reservamos el derecho de
          modificar estos términos en cualquier momento. Los cambios serán
          efectivos desde su publicación.
        </p>
      </LegalModal>
    </>;
}